import React, { useState, useEffect, useRef } from 'react';
import { 
  Upload, FileText, Wand2, CheckCircle2, XCircle, AlertCircle,
  Loader2, File, Trash2, ChevronDown, ChevronUp, Eye, CheckSquare,
  Square, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentAutoFill = ({ assessmentId, assessmentType, onComplete }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [selectedSuggestions, setSelectedSuggestions] = useState({});
  const [mode, setMode] = useState('review'); // 'review' or 'autofill'
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('cset_token');
    return {
      'Authorization': `Bearer ${token}`
    };
  };

  useEffect(() => {
    if (isOpen) {
      fetchDocuments();
    }
  }, [isOpen, assessmentId]);

  const fetchDocuments = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/document-autofill/documents/${assessmentId}`,
        { headers: getAuthHeaders() }
      );
      if (response.ok) {
        const data = await response.json();
        setDocuments(data.documents || []);
      }
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const validTypes = ['.pdf', '.docx'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExt)) {
      setError('Please upload a PDF or Word (.docx) file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('assessmentId', assessmentId);

    try {
      const response = await fetch(`${API_URL}/api/document-autofill/upload`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: formData
      });

      if (response.ok) {
        const data = await response.json();
        setDocuments([data.document, ...documents]);
        setSelectedDocument(data.document);
        setSuccess(`Document uploaded: ${data.totalCharacters.toLocaleString()} characters extracted`);
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Upload failed');
      }
    } catch (err) {
      setError('Failed to upload document');
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAnalyze = async () => {
    if (!selectedDocument) {
      setError('Please select a document first');
      return;
    }

    setAnalyzing(true);
    setError('');
    setSuccess('');
    setSuggestions([]);

    try {
      const response = await fetch(`${API_URL}/api/document-autofill/analyze`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assessmentId,
          documentId: selectedDocument.id,
          mode
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.suggestions || []);
        
        // Pre-select high confidence suggestions
        const preSelected = {};
        data.suggestions?.forEach(s => {
          if (s.confidence === 'high') {
            preSelected[s.questionId] = true;
          }
        });
        setSelectedSuggestions(preSelected);

        if (data.appliedDirectly) {
          setSuccess(`Auto-filled ${data.totalSuggestions} answers directly!`);
          if (onComplete) onComplete();
        } else {
          setSuccess(`Found ${data.totalSuggestions} suggested answers. Review and apply below.`);
        }
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Analysis failed');
      }
    } catch (err) {
      setError('Failed to analyze document');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApplySuggestions = async () => {
    const toApply = suggestions.filter(s => selectedSuggestions[s.questionId]);
    
    if (toApply.length === 0) {
      setError('Please select at least one suggestion to apply');
      return;
    }

    setAnalyzing(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/document-autofill/apply`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          assessmentId,
          suggestions: toApply.map(s => ({
            questionId: s.questionId,
            answer: s.answer,
            comments: s.evidence,
            confidence: s.confidence
          }))
        })
      });

      if (response.ok) {
        const data = await response.json();
        setSuccess(`Applied ${data.appliedCount} answers to the assessment!`);
        setSuggestions([]);
        setSelectedSuggestions({});
        if (onComplete) onComplete();
      } else {
        const errData = await response.json();
        setError(errData.detail || 'Failed to apply suggestions');
      }
    } catch (err) {
      setError('Failed to apply suggestions');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!confirm('Delete this document?')) return;

    try {
      const response = await fetch(`${API_URL}/api/document-autofill/documents/${docId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setDocuments(documents.filter(d => d.id !== docId));
        if (selectedDocument?.id === docId) {
          setSelectedDocument(null);
        }
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
    }
  };

  const toggleSuggestion = (questionId) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };

  const selectAllSuggestions = () => {
    const all = {};
    suggestions.forEach(s => { all[s.questionId] = true; });
    setSelectedSuggestions(all);
  };

  const deselectAllSuggestions = () => {
    setSelectedSuggestions({});
  };

  const getConfidenceColor = (confidence) => {
    switch(confidence) {
      case 'high': return 'text-emerald-400 bg-emerald-500/20';
      case 'medium': return 'text-amber-400 bg-amber-500/20';
      case 'low': return 'text-red-400 bg-red-500/20';
      default: return 'text-slate-400 bg-slate-500/20';
    }
  };

  const getAnswerColor = (answer) => {
    switch(answer) {
      case 'yes': return 'text-emerald-400';
      case 'no': return 'text-red-400';
      case 'na': return 'text-slate-400';
      default: return 'text-slate-400';
    }
  };

  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 overflow-hidden" data-testid="document-autofill">
      {/* Header - Always visible */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-800/70 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-violet-500/20 flex items-center justify-center">
            <Wand2 className="w-5 h-5 text-violet-400" />
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-white">Document Auto-Fill</h3>
            <p className="text-sm text-slate-400">Upload a PDF or Word doc to auto-fill answers</p>
          </div>
        </div>
        {isOpen ? (
          <ChevronUp className="w-5 h-5 text-slate-400" />
        ) : (
          <ChevronDown className="w-5 h-5 text-slate-400" />
        )}
      </button>

      {/* Expanded Content */}
      {isOpen && (
        <div className="p-4 border-t border-slate-700/50 space-y-4">
          {/* Error/Success Messages */}
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          {success && (
            <div className="flex items-center gap-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-400 text-sm">
              <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              {success}
            </div>
          )}

          {/* Upload Section */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-slate-300">Upload Document</label>
              <span className="text-xs text-slate-500">PDF or Word (.docx)</span>
            </div>
            
            <div className="flex gap-3">
              <label className="flex-1 cursor-pointer">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.docx"
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploading}
                />
                <div className="flex items-center justify-center gap-2 px-4 py-3 bg-slate-900 border-2 border-dashed border-slate-700 rounded-lg hover:border-violet-500/50 transition-colors">
                  {uploading ? (
                    <Loader2 className="w-5 h-5 text-violet-400 animate-spin" />
                  ) : (
                    <Upload className="w-5 h-5 text-slate-400" />
                  )}
                  <span className="text-sm text-slate-400">
                    {uploading ? 'Uploading...' : 'Click to upload'}
                  </span>
                </div>
              </label>
            </div>
          </div>

          {/* Previously Uploaded Documents */}
          {documents.length > 0 && (
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Select Document</label>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className={`flex items-center justify-between p-3 rounded-lg cursor-pointer transition-colors ${
                      selectedDocument?.id === doc.id
                        ? 'bg-violet-500/20 border border-violet-500/30'
                        : 'bg-slate-900 border border-transparent hover:bg-slate-800'
                    }`}
                    onClick={() => setSelectedDocument(doc)}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className={`w-5 h-5 ${
                        selectedDocument?.id === doc.id ? 'text-violet-400' : 'text-slate-500'
                      }`} />
                      <div>
                        <p className="text-sm text-white">{doc.filename}</p>
                        <p className="text-xs text-slate-500">
                          {doc.textLength?.toLocaleString()} chars • {doc.status}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDeleteDocument(doc.id); }}
                      className="p-1 text-slate-500 hover:text-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Mode Selection */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-slate-300">Fill Mode</label>
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => setMode('review')}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  mode === 'review'
                    ? 'bg-violet-500/20 border-violet-500/30 text-white'
                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <Eye className={`w-5 h-5 mb-1 ${mode === 'review' ? 'text-violet-400' : ''}`} />
                <p className="text-sm font-medium">Review Mode</p>
                <p className="text-xs opacity-70">Review suggestions before applying</p>
              </button>
              <button
                onClick={() => setMode('autofill')}
                className={`p-3 rounded-lg border text-left transition-colors ${
                  mode === 'autofill'
                    ? 'bg-emerald-500/20 border-emerald-500/30 text-white'
                    : 'bg-slate-900 border-slate-700 text-slate-400 hover:bg-slate-800'
                }`}
              >
                <Wand2 className={`w-5 h-5 mb-1 ${mode === 'autofill' ? 'text-emerald-400' : ''}`} />
                <p className="text-sm font-medium">Auto-Fill Mode</p>
                <p className="text-xs opacity-70">Fill answers directly</p>
              </button>
            </div>
          </div>

          {/* Analyze Button */}
          <button
            onClick={handleAnalyze}
            disabled={!selectedDocument || analyzing}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="analyze-btn"
          >
            {analyzing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyzing with AI...
              </>
            ) : (
              <>
                <Wand2 className="w-5 h-5" />
                Analyze Document
              </>
            )}
          </button>

          {/* Suggestions List (Review Mode) */}
          {suggestions.length > 0 && mode === 'review' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-300">
                  Suggested Answers ({Object.values(selectedSuggestions).filter(Boolean).length}/{suggestions.length} selected)
                </label>
                <div className="flex gap-2">
                  <button
                    onClick={selectAllSuggestions}
                    className="text-xs text-violet-400 hover:text-violet-300"
                  >
                    Select All
                  </button>
                  <button
                    onClick={deselectAllSuggestions}
                    className="text-xs text-slate-400 hover:text-slate-300"
                  >
                    Clear
                  </button>
                </div>
              </div>

              <div className="space-y-2 max-h-80 overflow-y-auto">
                {suggestions.map((suggestion) => (
                  <div
                    key={suggestion.questionId}
                    className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                      selectedSuggestions[suggestion.questionId]
                        ? 'bg-violet-500/10 border-violet-500/30'
                        : 'bg-slate-900 border-slate-700 hover:bg-slate-800'
                    }`}
                    onClick={() => toggleSuggestion(suggestion.questionId)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5">
                        {selectedSuggestions[suggestion.questionId] ? (
                          <CheckSquare className="w-5 h-5 text-violet-400" />
                        ) : (
                          <Square className="w-5 h-5 text-slate-500" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`text-sm font-bold uppercase ${getAnswerColor(suggestion.answer)}`}>
                            {suggestion.answer}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs ${getConfidenceColor(suggestion.confidence)}`}>
                            {suggestion.confidence}
                          </span>
                          {suggestion.domain && (
                            <span className="text-xs text-slate-500">{suggestion.domain}</span>
                          )}
                        </div>
                        <p className="text-sm text-white mb-1 line-clamp-2">{suggestion.questionText}</p>
                        {suggestion.evidence && (
                          <p className="text-xs text-slate-400 italic line-clamp-2">
                            "{suggestion.evidence}"
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleApplySuggestions}
                disabled={Object.values(selectedSuggestions).filter(Boolean).length === 0 || analyzing}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-lg transition-colors disabled:opacity-50"
              >
                {analyzing ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <CheckCircle2 className="w-5 h-5" />
                )}
                Apply {Object.values(selectedSuggestions).filter(Boolean).length} Selected Answers
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentAutoFill;
